import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import routesfe from "../../Config/RoutesFE";
import noteforgelogo from "../../Landing/Images/noteforgelogo.jpg";
import "../../Styles/PayPalSuccess.css";

const PayPalFailure = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const { error, orderNumber, plan, status } = location.state || {};
  
  const cancelled = params.get("cancelled") === "true";
  const displayError = error || (cancelled ? "Payment was cancelled." : "PayPal payment could not be completed.");

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-indigo-50/50 to-blue-50 flex items-center justify-center p-4">
      {/* Decorative floating elements */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Floating dots */}
        <div className="absolute top-20 right-20 w-3 h-3 bg-red-300 rounded-full animate-pulse" style={{ animationDelay: '0s' }} />
        <div className="absolute top-40 right-40 w-2 h-2 bg-orange-300 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute bottom-32 right-24 w-2.5 h-2.5 bg-violet-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-32 left-32 w-2 h-2 bg-indigo-300 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
        <div className="absolute bottom-40 left-20 w-3 h-3 bg-red-200 rounded-full animate-pulse" style={{ animationDelay: '0.7s' }} />
        <div className="absolute top-1/2 right-16 w-2 h-2 bg-orange-200 rounded-full animate-pulse" style={{ animationDelay: '1.2s' }} />
        
        {/* Decorative rings */}
        <div className="absolute top-24 left-1/4 w-16 h-16 border border-red-200/40 rounded-full" />
        <div className="absolute bottom-24 right-1/4 w-20 h-20 border border-orange-200/30 rounded-full" />
        <div className="absolute top-1/3 right-1/3 w-12 h-12 border border-violet-200/30 rounded-full" />
        
        {/* Subtle gradient orbs */}
        <div className="absolute top-16 right-1/4 w-32 h-32 bg-gradient-to-br from-red-100/20 to-transparent rounded-full blur-2xl" />
        <div className="absolute bottom-20 left-1/4 w-40 h-40 bg-gradient-to-br from-orange-100/20 to-transparent rounded-full blur-2xl" />
        
        {/* Floating orbs */}
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-gradient-to-br from-red-100/30 to-orange-100/20 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute top-1/3 -left-24 w-[400px] h-[400px] bg-gradient-to-br from-violet-100/30 to-indigo-100/20 rounded-full blur-3xl animate-float-delayed" />
      </div>

      {/* NoteForge Header - Top Left */}
      <div className="fixed top-6 left-6 z-50">
        <div className="flex items-center gap-3">
          <img src={noteforgelogo} alt="NoteForge" className="w-12 h-12 object-contain" />
          <div className="flex flex-col">
            <span className="font-bold text-slate-900 text-lg tracking-wide">NOTEFORGE</span>
            <span className="text-xs text-purple-600 flex items-center gap-1">
              <span className="text-amber-500">✨</span>
              Capture. Structure. Succeed.
            </span>
          </div>
        </div>
      </div>

      <div className="relative max-w-md w-full z-10">
        <div className="bg-white/90 backdrop-blur-2xl rounded-2xl shadow-2xl border border-white/70 overflow-hidden">
          <div className="bg-gradient-to-r from-red-500 via-red-600 to-orange-600 px-5 py-4 text-center relative overflow-hidden">
            {/* Shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent" style={{ animation: 'shimmer 3s infinite' }} />
            
            <div className="relative z-10">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-white rounded-full shadow-lg mb-2">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              
              <h1 className="text-lg font-bold text-white mb-0.5">
                {cancelled ? "Payment Cancelled" : "PayPal Payment Failed"}
              </h1>
              <p className="text-red-100 text-xs">
                {cancelled ? "You cancelled the payment" : "We couldn't process your PayPal payment"}
              </p>
            </div>
          </div>

          <div className="p-5">
            <div className="bg-red-50 rounded-xl p-3.5 border border-red-100 mb-4">
              <div className="space-y-0">
                <div className="py-1.5 border-b border-red-200/70">
                  <span className="text-slate-500 text-[11px] block mb-1">Reason</span>
                  <span className="text-slate-900 text-sm">{displayError}</span>
                </div>

                {orderNumber && (
                  <div className="flex justify-between items-center py-1.5 border-b border-red-200/70">
                    <span className="text-slate-500 text-[11px]">Order Number</span>
                    <span className="text-slate-900 font-mono text-[11px] font-medium">{orderNumber}</span>
                  </div>
                )}

                {plan?.name && (
                  <div className="flex justify-between items-center py-1.5 border-b border-red-200/70">
                    <span className="text-slate-500 text-[11px]">Plan</span>
                    <span className="text-slate-900 text-[11px] font-medium">{plan.name}</span>
                  </div>
                )}

                {plan?.price && (
                  <div className="flex justify-between items-center py-1.5 border-b border-red-200/70">
                    <span className="text-slate-500 text-[11px]">Amount</span>
                    <span className="text-red-600 font-semibold text-sm">
                      {plan.currencySymbol || '$'}{plan.price}
                    </span>
                  </div>
                )}

                <div className="flex justify-between items-center py-1.5 border-b border-red-200/70">
                  <span className="text-slate-500 text-[11px]">Payment Method</span>
                  <div className="flex items-center gap-1.5">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z" fill="#003087"/>
                      <path d="M21.222 6.917a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z" fill="#0070E0"/>
                    </svg>
                    <span className="text-[11px] font-semibold text-slate-900">PayPal</span>
                  </div>
                </div>

                {status && (
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-slate-500 text-[11px]">Status</span>
                    <span className="text-red-600 font-semibold text-[11px] uppercase">{status}</span>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={() => navigate(routesfe.PLANS)}
              className="w-full bg-gradient-to-r from-violet-600 via-indigo-600 to-blue-600 hover:from-violet-700 hover:via-indigo-700 hover:to-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 shadow-lg flex items-center justify-center gap-2 text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          </div>
        </div>

        <div className="mt-4 text-center">
          <p className="text-slate-400 text-xs">
            Need help?{' '}
            <a 
              href="mailto:support@noteforge.ai" 
              className="font-medium text-violet-500 hover:text-violet-600 transition-colors underline underline-offset-2"
            >
              Contact Us
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PayPalFailure;
